import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SELECTED_ACCOUNTS_KEY = "selectedAccountAlignedList";
const ACCOUNT_OPTIONS_API_BASE_URL = `${(
  process.env.REACT_APP_API_URL || ""
).replace(/\/$/, "")}/api/user-management/account-aligned-options`;

const AccountsList = () => {
  const navigate = useNavigate();
  const [accountAlignedOptions, setAccountAlignedOptions] = useState<string[]>([]);
  const [privateAccounts, setPrivateAccounts] = useState<string[]>([]);
  const [sharedAccounts, setSharedAccounts] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SELECTED_ACCOUNTS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setSelectedAccounts(
            parsed.filter(
              (item): item is string =>
                typeof item === "string" && item.trim().length > 0,
            ),
          );
        }
      }
    } catch {
      // Ignore malformed storage.
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchAccounts = async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        const roleName = localStorage.getItem("userRole") || "";
        const response = await axios.get(
          `${ACCOUNT_OPTIONS_API_BASE_URL}?role_name=${encodeURIComponent(roleName)}`,
          { withCredentials: true },
        );

        const shared = Array.isArray(response.data?.shared_firms)
          ? response.data.shared_firms.filter(
              (item: unknown): item is string =>
                typeof item === "string" && item.trim().length > 0,
            )
          : [];

        const privateList = Array.isArray(response.data?.private_firms)
          ? response.data.private_firms.filter(
              (item: unknown): item is string =>
                typeof item === "string" && item.trim().length > 0,
            )
          : [];

        if (isMounted) {
          setAccountAlignedOptions(privateList);
          setSharedAccounts(shared);
          setPrivateAccounts(privateList);
          setSelectedAccounts((prev) => {
            if (prev.length === 0 && shared.length > 0) {
              localStorage.setItem(SELECTED_ACCOUNTS_KEY, JSON.stringify(shared));
              return shared;
            }
            return prev;
          });
        }
      } catch (error) {
        if (isMounted) {
          setSharedAccounts([]);
          setPrivateAccounts([]);
          const status = (error as { response?: { status?: unknown } })?.response?.status;
          setErrorMessage(
            status === 401
              ? "Session expired. Please login again."
              : "Unable to load accounts. Please try again.",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchAccounts();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAccountSelect = (account: string) => {
    const next = [...sharedAccounts, account];
    localStorage.setItem(SELECTED_ACCOUNTS_KEY, JSON.stringify(next));
    setSelectedAccounts(next);
    setSelectedAccount(account);
  };

  const handleContinue = () => {
    if (selectedAccounts.length === 0) {
      return;
    }

    localStorage.setItem(SELECTED_ACCOUNTS_KEY, JSON.stringify(selectedAccounts));
    navigate("/options");
  };

  return (
    <div className="min-h-screen bg-[#f4fbf6] dark:bg-gray-950 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-emerald-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -top-40 -right-32 w-[440px] h-[440px] rounded-full bg-sky-200/25 blur-3xl" />
      <div className="pointer-events-none absolute bottom-40 right-1/4 w-[380px] h-[380px] rounded-full bg-indigo-200/20 blur-3xl" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-green-600 dark:text-white mb-2">
            Select Account
          </h2>
        </div>

        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-md">
          {loading && (
            <div className="py-8 text-center text-gray-600 dark:text-gray-300">
              Loading accounts...
            </div>
          )}

          {!loading && errorMessage && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
              {errorMessage}
            </div>
          )}

          {!loading && !errorMessage && sharedAccounts.length === 0 && privateAccounts.length === 0 && (
            <div className="py-8 text-center text-gray-600 dark:text-gray-300">
              No accounts found.
            </div>
          )}

          {!loading && accountAlignedOptions.length > 0 && (
            <>
              <div className="mb-6"></div>
              <div className="space-y-2">
                {accountAlignedOptions.map((account) => {
                  const isSelected = selectedAccount === account;

                  return (
                    <label
                      key={account}
                      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 font-medium transition-colors cursor-pointer ${
                        isSelected
                          ? "border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                          : "border-gray-200 bg-white text-gray-800 hover:border-emerald-400 hover:bg-emerald-50/50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-emerald-500 dark:hover:bg-emerald-950/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name="accountSelection"
                        checked={isSelected}
                        onChange={() => handleAccountSelect(account)}
                        className="h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span>{account}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleContinue}
              disabled={selectedAccounts.length === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountsList;